import * as React from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";

import { api } from "$convex/convexApi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { BADGE_TONES } from "@/lib/badge-tones";
import { formatDate, formatStatusLabel } from "@/lib/format";
import { hasPermission } from "@/lib/roles";
import { cn } from "@/lib/utils";

const USESEND_UNSUBSCRIBE_PLACEHOLDER = "{{usesend_unsubscribe_url}}";
const ANTHEM_MARKETING_PREFERENCES_PLACEHOLDER = "{{anthem_marketing_preferences_url}}";
const USESEND_UNSUBSCRIBE_SNIPPET = `<a href="${USESEND_UNSUBSCRIBE_PLACEHOLDER}">Unsubscribe</a>`;
const ANTHEM_MARKETING_PREFERENCES_SNIPPET = `<a href="${ANTHEM_MARKETING_PREFERENCES_PLACEHOLDER}">Manage email preferences</a>`;
const DEFAULT_TEST_RECIPIENT_EMAIL = "itisyb@gmail.com";

function statusTone(status: string): string {
  const normalized = status.trim().toLowerCase();
  if (normalized === "sent") return BADGE_TONES.sky;
  if (normalized === "failed" || normalized === "bounced") return BADGE_TONES.red;
  return BADGE_TONES.slate;
}

function campaignStatusTone(status: string): string {
  const normalized = status.trim().toLowerCase();
  if (normalized === "sent") return BADGE_TONES.sky;
  if (normalized === "failed") return BADGE_TONES.red;
  return BADGE_TONES.slate;
}

function detectRiskyHtmlTags(html: string): string[] {
  return ["script", "iframe", "object", "embed", "form"].filter((tag) =>
    new RegExp(`<\\s*${tag}(\\s|>|/)`, "i").test(html),
  );
}

function buildDraftKey({
  name,
  subject,
  previewText,
  html,
}: {
  name: string;
  subject: string;
  previewText: string;
  html: string;
}) {
  return JSON.stringify({
    name: name.trim(),
    subject: subject.trim(),
    previewText: previewText.trim(),
    html,
  });
}

type DeliveryMode = "contact_book" | "selected_customers";

function buildGateKey(deliveryMode: DeliveryMode, draftKey: string): string {
  return `${deliveryMode}:${draftKey}`;
}

type SelectedCustomerSummary = {
  id: string;
  label: string;
};

function getCustomerId(customer: any): string {
  return String(customer?._id ?? "");
}

function normalizeCustomerText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function isCustomerMarketingEligible(customer: any): boolean {
  return Boolean(normalizeCustomerText(customer?.email) && customer?.acceptsMarketing === true);
}

function buildCustomerLabel(customer: any): string {
  const parts = [
    normalizeCustomerText(customer?.company),
    normalizeCustomerText(customer?.name),
    normalizeCustomerText(customer?.email),
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : getCustomerId(customer);
}

function buildSelectedCustomerSummary(customer: any): SelectedCustomerSummary {
  return {
    id: getCustomerId(customer),
    label: buildCustomerLabel(customer),
  };
}

function StatCard({
  title,
  value,
  description,
}: {
  title: string;
  value: string | number;
  description: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl tabular-nums">{value}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

function EmailsPageNav({ active }: { active: "activity" | "create" }) {
  const items = [
    {
      key: "activity" as const,
      label: "Activity",
      href: "/marketing/emails",
    },
    {
      key: "create" as const,
      label: "Create campaign",
      href: "/marketing/emails/create",
    },
  ];

  return (
    <nav className="flex gap-1 border-b">
      {items.map((item) => {
        const isActive = item.key === active;
        return (
          <Link
            key={item.key}
            to={item.href}
            aria-current={isActive ? "page" : undefined}
            className={cn(
              "relative -mb-px px-4 py-2.5 text-sm font-medium transition-colors",
              isActive
                ? "text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

function CampaignCreationGuide() {
  const steps = ["Compose", "Proof", "Send"];

  return (
    <p className="text-sm text-muted-foreground">
      <span className="font-medium text-foreground">How it works:</span>{" "}
      {steps.map((step, index) => (
        <React.Fragment key={step}>
          {index > 0 ? <span className="mx-1.5 text-border/80">→</span> : null}
          <span>
            {index + 1}. {step}
          </span>
        </React.Fragment>
      ))}
    </p>
  );
}

function RequirementBadge({ required }: { required: boolean }) {
  if (required) {
    return (
      <span className="text-destructive" aria-hidden="true">
        *
      </span>
    );
  }

  return <span className="text-xs font-normal text-muted-foreground">(optional)</span>;
}

function workflowChoiceClass(selected: boolean) {
  return cn(
    "w-full cursor-pointer rounded-lg border p-4 text-left transition-[background-color,border-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]",
    selected
      ? "border-primary bg-primary/5 shadow-[0_0_0_1px_hsl(var(--primary))]"
      : "border-border bg-background shadow-sm hover:border-primary/40 hover:bg-muted/50",
  );
}

function WorkflowRadio({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
        selected ? "border-primary bg-primary" : "border-muted-foreground/40 bg-background",
      )}
      aria-hidden="true"
    >
      {selected ? <span className="size-2 rounded-full bg-primary-foreground" /> : null}
    </span>
  );
}

type UseSendLiveInfo = {
  contactBooks: Array<{
    target: string;
    label: string;
    configured: boolean;
    name: string;
    contacts: number;
    variables: string[];
    doubleOptInEnabled: boolean;
    updatedAt: string | null;
  }>;
};

function CampaignComposer({
  authUserId,
  consoleData,
  liveInfo,
  isLoadingLiveInfo,
  resumeCampaign,
}: {
  authUserId: string | undefined;
  consoleData: any;
  liveInfo?: UseSendLiveInfo | null;
  isLoadingLiveInfo?: boolean;
  resumeCampaign?: any | null;
}) {
  const createCampaign = useAction(
    (api.marketing as any).createUseSendSelfServeCampaign,
  );
  const [name, setName] = React.useState("");
  const [subject, setSubject] = React.useState("");
  const [previewText, setPreviewText] = React.useState("");
  const [html, setHtml] = React.useState("");
  const [confirmSend, setConfirmSend] = React.useState("");
  const [testRecipientEmail, setTestRecipientEmail] = React.useState(DEFAULT_TEST_RECIPIENT_EMAIL);
  const [riskyHtmlOverride, setRiskyHtmlOverride] = React.useState(false);
  const [duplicateProductionOverride, setDuplicateProductionOverride] = React.useState(false);
  const [testedDraftKey, setTestedDraftKey] = React.useState<string | null>(null);
  const [isPreviewInteractive, setIsPreviewInteractive] = React.useState(false);
  const [isSendingTest, setIsSendingTest] = React.useState(false);
  const [isSendingProduction, setIsSendingProduction] = React.useState(false);
  const [deliveryMode, setDeliveryMode] = React.useState<DeliveryMode>("contact_book");
  const [customerSearch, setCustomerSearch] = React.useState("");
  const [selectedCustomers, setSelectedCustomers] = React.useState<
    Record<string, SelectedCustomerSummary>
  >({});
  const loadedResumeCampaignIdRef = React.useRef<string | null>(null);

  const draftKey = React.useMemo(
    () => buildDraftKey({ name, subject, previewText, html }),
    [html, name, previewText, subject],
  );
  const riskyHtmlTags = React.useMemo(() => detectRiskyHtmlTags(html), [html]);
  const hasMarketingOptOutLink =
    html.includes(ANTHEM_MARKETING_PREFERENCES_PLACEHOLDER) ||
    html.includes(USESEND_UNSUBSCRIBE_PLACEHOLDER) ||
    html.includes("/dashboard/profile");
  const productionReady = testedDraftKey === buildGateKey(deliveryMode, draftKey);
  const testBook = consoleData?.contactBooks?.find((book: any) => book.target === "test");
  const productionBook = consoleData?.contactBooks?.find(
    (book: any) => book.target === "production",
  );
  const liveTestBook = liveInfo?.contactBooks.find((book) => book.target === "test");
  const liveProductionBook = liveInfo?.contactBooks.find(
    (book) => book.target === "production",
  );
  const testTargetConfigured = liveTestBook?.configured ?? Boolean(testBook?.id);
  const productionTargetConfigured = liveProductionBook?.configured ?? Boolean(productionBook?.id);
  const isContactBookMode = deliveryMode === "contact_book";
  const isSelectedCustomerMode = deliveryMode === "selected_customers";
  const selectedCustomerList = React.useMemo(
    () => Object.values(selectedCustomers),
    [selectedCustomers],
  );
  const selectedCustomerIds = React.useMemo(
    () => selectedCustomerList.map((customer) => customer.id),
    [selectedCustomerList],
  );
  const customerAudience = useQuery(
    (api.marketing as any).getUsersInsights,
    authUserId && isSelectedCustomerMode
      ? { authUserId, limit: 100, search: customerSearch }
      : "skip",
  );
  const visibleCustomers = customerAudience?.users ?? [];
  const visibleEligibleCustomers = React.useMemo(
    () => visibleCustomers.filter((customer: any) => isCustomerMarketingEligible(customer)),
    [visibleCustomers],
  );

  React.useEffect(() => {
    if (!resumeCampaign) {
      loadedResumeCampaignIdRef.current = null;
      return;
    }

    const resumeCampaignId = String(resumeCampaign._id ?? "");
    if (!resumeCampaignId || loadedResumeCampaignIdRef.current === resumeCampaignId) return;
    loadedResumeCampaignIdRef.current = resumeCampaignId;

    const resumedHtml = typeof resumeCampaign.html === "string" ? resumeCampaign.html : "";
    if (!resumedHtml.trim()) {
      toast.error(
        resumeCampaign.resumeUnavailableReason ||
          "Full campaign HTML is unavailable. Re-paste the approved HTML to send production.",
      );
      return;
    }

    const resumedName = String(resumeCampaign.name ?? "");
    const resumedSubject = String(resumeCampaign.subject ?? "");
    const resumedPreviewText = String(resumeCampaign.previewText ?? "");
    const resumedDraftKey = buildDraftKey({
      name: resumedName,
      subject: resumedSubject,
      previewText: resumedPreviewText,
      html: resumedHtml,
    });
    const passedTestGate =
      String(resumeCampaign.target ?? "") === "test" &&
      String(resumeCampaign.status ?? "").toLowerCase() === "sent";
    const resumedDeliveryMode: DeliveryMode = String(resumeCampaign.contactBookId ?? "").startsWith("single:")
      ? "selected_customers"
      : "contact_book";

    setName(resumedName);
    setSubject(resumedSubject);
    setPreviewText(resumedPreviewText);
    setHtml(resumedHtml);
    setConfirmSend("");
    setDuplicateProductionOverride(false);
    setRiskyHtmlOverride(Array.isArray(resumeCampaign.riskyHtmlTags) && resumeCampaign.riskyHtmlTags.length > 0);
    setDeliveryMode(resumedDeliveryMode);
    setTestedDraftKey(passedTestGate ? buildGateKey(resumedDeliveryMode, resumedDraftKey) : null);
    toast.success(
      passedTestGate
        ? "Loaded the tested draft. Type SEND to send it to Production."
        : "Loaded campaign draft.",
    );
  }, [resumeCampaign]);

  React.useEffect(() => {
    setDuplicateProductionOverride(false);
  }, [draftKey]);

  const handleInsertOptOutLink = React.useCallback((snippet: string, placeholder: string) => {
    setHtml((currentHtml) => {
      if (currentHtml.includes(placeholder)) return currentHtml;
      const separator = currentHtml.trim() ? "\n\n" : "";
      return `${currentHtml}${separator}<p>${snippet}</p>`;
    });
  }, []);

  const toggleSelectedCustomer = React.useCallback((customer: any) => {
    const id = getCustomerId(customer);
    if (!id || !isCustomerMarketingEligible(customer)) return;
    setSelectedCustomers((current) => {
      if (current[id]) {
        const { [id]: _removed, ...remaining } = current;
        return remaining;
      }
      return {
        ...current,
        [id]: buildSelectedCustomerSummary(customer),
      };
    });
  }, []);

  const selectVisibleEligibleCustomers = React.useCallback(() => {
    setSelectedCustomers((current) => {
      const next = { ...current };
      for (const customer of visibleEligibleCustomers) {
        const summary = buildSelectedCustomerSummary(customer);
        if (summary.id) next[summary.id] = summary;
      }
      return next;
    });
  }, [visibleEligibleCustomers]);

  const clearSelectedCustomers = React.useCallback(() => {
    setSelectedCustomers({});
  }, []);

  const validateDraft = React.useCallback(
    (target: "test" | "production") => {
      if (!authUserId) {
        toast.error("Authentication required");
        return false;
      }
      if (!name.trim() || !subject.trim() || !html.trim()) {
        toast.error("Campaign name, subject, and HTML are required.");
        return false;
      }
      if (!hasMarketingOptOutLink) {
        toast.error(`HTML must include ${ANTHEM_MARKETING_PREFERENCES_PLACEHOLDER}.`);
        return false;
      }
      if (riskyHtmlTags.length > 0 && !riskyHtmlOverride) {
        toast.error("Risky HTML tags detected. Review them and check the override box to continue.");
        return false;
      }
      if (target === "test" && isSelectedCustomerMode && !testRecipientEmail.trim()) {
        toast.error("Enter a preview recipient email.");
        return false;
      }
      if (target === "production" && !productionReady) {
        toast.error(
          isContactBookMode
            ? "Send this exact draft to the Test Contact Book before Production."
            : "Send this exact draft as a preview email before selected customers.",
        );
        return false;
      }
      if (target === "production" && confirmSend.trim() !== "SEND") {
        toast.error(
          isContactBookMode
            ? "Type SEND to confirm the Production Contact Book send."
            : "Type SEND to confirm the selected-customer send.",
        );
        return false;
      }
      if (target === "production" && isSelectedCustomerMode && selectedCustomerIds.length === 0) {
        toast.error("Select at least one opted-in customer.");
        return false;
      }
      return true;
    },
    [
      authUserId,
      confirmSend,
      hasMarketingOptOutLink,
      html,
      isContactBookMode,
      isSelectedCustomerMode,
      name,
      productionReady,
      riskyHtmlOverride,
      riskyHtmlTags.length,
      selectedCustomerIds.length,
      subject,
      testRecipientEmail,
    ],
  );

  const handleSend = React.useCallback(
    async (target: "test" | "production") => {
      if (!validateDraft(target) || !authUserId) return;

      if (target === "test") {
        setIsSendingTest(true);
      } else {
        setIsSendingProduction(true);
      }

      try {
        const campaignTarget =
          target === "production" && isSelectedCustomerMode
            ? "custom"
            : target;
        const result = await createCampaign({
          authUserId,
          target: campaignTarget,
          deliveryMode,
          name,
          subject,
          previewText,
          html,
          riskyHtmlOverride,
          duplicateProductionOverride,
          productionConfirmation: target === "production" ? confirmSend.trim() : undefined,
          selectedUserProfileIds: campaignTarget === "custom" ? selectedCustomerIds : undefined,
          testRecipientEmail: target === "test" && isSelectedCustomerMode ? testRecipientEmail.trim() : undefined,
        });

        if (result?.requiresRiskyHtmlOverride) {
          toast.error(`Risky HTML tags detected: ${result.riskyHtmlTags?.join(", ")}`);
          return;
        }

        if (result?.requiresProductionInFlightWait) {
          setDuplicateProductionOverride(false);
          toast.error("This exact campaign is already being sent to Production. Wait for it to finish before trying again.");
          return;
        }

        if (result?.requiresDuplicateProductionOverride) {
          setDuplicateProductionOverride(true);
          toast.error("This exact campaign was already sent to Production. Click Production send again to continue.");
          return;
        }

        if (result?.success === false) {
          toast.error(String(result.message ?? "Campaign send failed."));
          return;
        }

        if (result?.success) {
          const campaignId = result?.campaign?.useSendCampaignId;
          if (target === "test") {
            setTestedDraftKey(buildGateKey(deliveryMode, draftKey));
            setConfirmSend("");
            setDuplicateProductionOverride(false);
            toast.success(
              isContactBookMode
                ? "Test Contact Book campaign sent."
                : `Preview email sent to ${testRecipientEmail.trim()}.`,
            );
          } else {
            setConfirmSend("");
            setDuplicateProductionOverride(false);
            toast.success(
              campaignId
                ? `Production Contact Book campaign sent. useSend campaign: ${campaignId}`
                : isSelectedCustomerMode
                  ? "Selected-customer send queued."
                  : "Production Contact Book campaign sent.",
            );
          }
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Campaign send failed");
      } finally {
        setIsSendingTest(false);
        setIsSendingProduction(false);
      }
    },
    [
      authUserId,
      confirmSend,
      createCampaign,
      draftKey,
      duplicateProductionOverride,
      html,
      name,
      deliveryMode,
      previewText,
      isContactBookMode,
      isSelectedCustomerMode,
      riskyHtmlOverride,
      selectedCustomerIds,
      subject,
      testRecipientEmail,
      validateDraft,
    ],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create marketing send</CardTitle>
        <CardDescription>
          Pick a workflow, prove the draft, then confirm production.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="flex items-center gap-1 text-sm font-medium" htmlFor="campaign-name">
              Campaign name
              <RequirementBadge required />
            </label>
            <Input
              id="campaign-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="May newsletter — Ethiopia arrivals"
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-1 text-sm font-medium" htmlFor="campaign-subject">
              Subject
              <RequirementBadge required />
            </label>
            <Input
              id="campaign-subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="New coffees available now"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <label className="flex items-center gap-1 text-sm font-medium" htmlFor="campaign-preview">
              Preview text
              <RequirementBadge required={false} />
            </label>
            <Input
              id="campaign-preview"
              value={previewText}
              onChange={(event) => setPreviewText(event.target.value)}
              placeholder="Short inbox preview text"
            />
          </div>
        </div>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">Send workflow</legend>
          <p className="text-sm text-muted-foreground">Choose one path. The other options below update to match.</p>
          <div className="grid gap-3 md:grid-cols-2">
            <button
              type="button"
              aria-pressed={isContactBookMode}
              onClick={() => setDeliveryMode("contact_book")}
              className={workflowChoiceClass(isContactBookMode)}
            >
              <span className="flex items-start gap-3">
                <WorkflowRadio selected={isContactBookMode} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">Batch campaign</span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    Test Contact Book, then Production Contact Book.
                  </span>
                </span>
              </span>
            </button>
            <button
              type="button"
              aria-pressed={isSelectedCustomerMode}
              onClick={() => setDeliveryMode("selected_customers")}
              className={workflowChoiceClass(isSelectedCustomerMode)}
            >
              <span className="flex items-start gap-3">
                <WorkflowRadio selected={isSelectedCustomerMode} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">Selected customers</span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    Send only to chosen opted-in customers.
                  </span>
                </span>
              </span>
            </button>
          </div>
        </fieldset>

        {isContactBookMode ? (
          <div className="rounded-lg bg-muted/40 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Contact-book audiences
            </p>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm font-medium">Test Contact Book</p>
                <p className="mt-0.5 text-sm text-muted-foreground">Safe proof audience.</p>
                <p className="mt-2 text-xs tabular-nums text-muted-foreground">
                  {isLoadingLiveInfo
                    ? "Fetching useSend audience..."
                    : liveTestBook
                      ? `${liveTestBook.name} · ${liveTestBook.contacts.toLocaleString()} contacts`
                      : "Configured in Anthem. Live useSend details unavailable."}
                </p>
                <p
                  className={cn(
                    "mt-1 text-xs",
                    testTargetConfigured ? "text-muted-foreground" : "text-destructive",
                  )}
                >
                  {testTargetConfigured ? "Ready" : "Setup missing"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium">Production Contact Book</p>
                <p className="mt-0.5 text-sm text-muted-foreground">Unlocks after the test proof.</p>
                <p className="mt-2 text-xs tabular-nums text-muted-foreground">
                  {isLoadingLiveInfo
                    ? "Fetching useSend audience..."
                    : liveProductionBook
                      ? `${liveProductionBook.name} · ${liveProductionBook.contacts.toLocaleString()} contacts`
                      : "Configured in Anthem. Live useSend details unavailable."}
                </p>
                <p
                  className={cn(
                    "mt-1 text-xs",
                    productionTargetConfigured ? "text-muted-foreground" : "text-destructive",
                  )}
                >
                  {productionTargetConfigured ? "Ready" : "Setup missing"}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)]">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="test-recipient-email">
                  Preview recipient
                </label>
                <p className="text-sm text-muted-foreground">
                  One proof email unlocks the selected-customer send.
                </p>
                <Input
                  id="test-recipient-email"
                  value={testRecipientEmail}
                  onChange={(event) => setTestRecipientEmail(event.target.value)}
                  placeholder={DEFAULT_TEST_RECIPIENT_EMAIL}
                />
              </div>
              <div className="rounded-lg bg-muted/40 px-4 py-3">
                <p className="text-sm font-medium">Selected-customer send</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Only checked opted-in customers below receive this email.
                </p>
                <p className="mt-2 text-xs tabular-nums text-muted-foreground">
                  {selectedCustomerList.length.toLocaleString()} selected
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <p className="text-sm font-medium">Customer audience</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={selectVisibleEligibleCustomers}
                    disabled={!visibleEligibleCustomers.length}
                  >
                    Select visible eligible
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSelectedCustomers}
                    disabled={!selectedCustomerList.length}
                  >
                    Clear selection
                  </Button>
                </div>
              </div>

              <Input
                value={customerSearch}
                onChange={(event) => setCustomerSearch(event.target.value)}
                placeholder="Search customers by email, company, state, sales rep..."
              />

              {customerAudience === undefined ? (
                <p className="py-6 text-center text-sm text-muted-foreground">Loading customers...</p>
              ) : !visibleCustomers.length ? (
                <p className="py-6 text-center text-sm text-muted-foreground">No customers match this search.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto rounded-lg border">
                  {visibleCustomers.map((customer: any) => {
                    const customerId = getCustomerId(customer);
                    const eligible = isCustomerMarketingEligible(customer);
                    const checked = Boolean(selectedCustomers[customerId]);
                    const email = normalizeCustomerText(customer.email);
                    const company = normalizeCustomerText(customer.company);
                    const nameValue = normalizeCustomerText(customer.name);
                    return (
                      <label
                        key={customerId}
                        className={cn(
                          "flex min-h-12 items-start gap-3 border-b p-3 last:border-b-0",
                          eligible
                            ? "cursor-pointer transition-colors hover:bg-muted/40"
                            : "text-muted-foreground opacity-70",
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!eligible}
                          onChange={() => toggleSelectedCustomer(customer)}
                          className="mt-1"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground">
                            {company || nameValue || email || customerId}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                            {[email, company && nameValue ? nameValue : ""].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                        {!eligible ? (
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {email ? "Not opted in" : "Missing email"}
                          </span>
                        ) : null}
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="flex flex-col gap-2 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between">
                <span>
                  Selected{" "}
                  <span className="font-medium tabular-nums text-foreground">
                    {selectedCustomerList.length.toLocaleString()}
                  </span>
                </span>
                {customerAudience?.totalMatching !== undefined ? (
                  <span className="tabular-nums">
                    Showing {visibleCustomers.length.toLocaleString()} of{" "}
                    {Number(customerAudience.totalMatching).toLocaleString()}
                  </span>
                ) : null}
              </div>

              {selectedCustomerList.length ? (
                <div className="flex max-h-24 flex-wrap gap-2 overflow-y-auto rounded-lg bg-muted/40 p-3">
                  {selectedCustomerList.slice(0, 20).map((customer) => (
                    <Badge key={customer.id} variant="secondary" className="max-w-full truncate">
                      {customer.label}
                    </Badge>
                  ))}
                  {selectedCustomerList.length > 20 ? (
                    <span className="text-xs text-muted-foreground">
                      +{(selectedCustomerList.length - 20).toLocaleString()} more
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <label className="flex items-center gap-1 text-sm font-medium" htmlFor="campaign-html">
              Raw HTML
              <RequirementBadge required />
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm">
                  Insert opt-out link
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuItem
                  onClick={() =>
                    handleInsertOptOutLink(
                      ANTHEM_MARKETING_PREFERENCES_SNIPPET,
                      ANTHEM_MARKETING_PREFERENCES_PLACEHOLDER,
                    )
                  }
                  disabled={html.includes(ANTHEM_MARKETING_PREFERENCES_PLACEHOLDER)}
                >
                  Customer profile preferences
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    handleInsertOptOutLink(
                      USESEND_UNSUBSCRIBE_SNIPPET,
                      USESEND_UNSUBSCRIBE_PLACEHOLDER,
                    )
                  }
                  disabled={html.includes(USESEND_UNSUBSCRIBE_PLACEHOLDER)}
                >
                  useSend unsubscribe placeholder
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Textarea
            id="campaign-html"
            value={html}
            onChange={(event) => setHtml(event.target.value)}
            placeholder={`Paste the full HTML. It must include ${ANTHEM_MARKETING_PREFERENCES_SNIPPET}.`}
            className="min-h-[260px] font-mono text-xs"
          />
          <p
            className={cn(
              "text-xs",
              hasMarketingOptOutLink ? "text-muted-foreground" : "text-destructive",
            )}
          >
            {hasMarketingOptOutLink ? "Opt-out link found" : "Missing marketing opt-out link"}
            <span className="text-muted-foreground">
              {" · "}
              {riskyHtmlTags.length ? `Risky tags: ${riskyHtmlTags.join(", ")}` : "No risky tags"}
              {" · "}
              {html.length.toLocaleString()} characters
            </span>
          </p>
        </div>

        {riskyHtmlTags.length ? (
          <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-200">
            <input
              type="checkbox"
              checked={riskyHtmlOverride}
              onChange={(event) => setRiskyHtmlOverride(event.target.checked)}
              className="mt-1"
            />
            <span>
              I reviewed the risky HTML tags ({riskyHtmlTags.join(", ")}) and want to send anyway.
            </span>
          </label>
        ) : null}

        <div className="space-y-2">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <p className="text-sm font-medium">HTML preview</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsPreviewInteractive((current) => !current)}
            >
              {isPreviewInteractive ? "Disable interaction" : "Enable interaction"}
            </Button>
          </div>
          <div className="overflow-hidden rounded-lg border bg-white">
            <iframe
              title="Campaign HTML preview"
              sandbox=""
              srcDoc={html || "<p style='font-family: sans-serif; color: #64748b;'>Paste HTML to preview it here.</p>"}
              tabIndex={isPreviewInteractive ? 0 : -1}
              className={isPreviewInteractive ? "h-[480px] w-full bg-white" : "pointer-events-none h-[480px] w-full bg-white"}
            />
          </div>
        </div>

        <div className="rounded-xl bg-muted/25 p-4 shadow-sm ring-1 ring-border/60 md:p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Send steps</p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <section
              className={cn(
                "flex flex-col rounded-xl bg-card p-5 shadow-sm ring-1 ring-black/5 dark:ring-white/10",
                productionReady && "ring-foreground/15",
              )}
            >
              <div className="flex items-center gap-2.5">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums text-muted-foreground">
                  1
                </span>
                <h3 className="text-sm font-medium">
                  {isContactBookMode ? "Send test" : "Send preview"}
                </h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {isContactBookMode
                  ? "Sends to the Test Contact Book only."
                  : `Sends one proof to ${testRecipientEmail.trim() || DEFAULT_TEST_RECIPIENT_EMAIL}.`}
              </p>
              <div className="mt-auto space-y-2 pt-5">
                <Button
                  className="shadow-sm"
                  onClick={() => handleSend("test")}
                  disabled={isSendingTest || isSendingProduction}
                >
                  {isSendingTest
                    ? "Sending test..."
                    : isContactBookMode
                      ? "Send Test Contact Book"
                      : "Send Preview Email"}
                </Button>
                {productionReady ? (
                  <p className="text-sm text-muted-foreground">Proof gate passed.</p>
                ) : null}
              </div>
            </section>

            <section className="flex flex-col rounded-xl bg-destructive/5 p-5 shadow-sm ring-1 ring-destructive/25 dark:bg-destructive/10">
              <div className="flex items-center gap-2.5">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-xs font-semibold tabular-nums text-destructive">
                  2
                </span>
                <h3 className="text-sm font-medium text-destructive">
                  Send {isContactBookMode ? "production" : "selected customers"}
                </h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {isContactBookMode
                  ? "Sends to the Production Contact Book."
                  : `Sends to ${selectedCustomerList.length.toLocaleString()} selected customers.`}{" "}
                Type <span className="font-mono">SEND</span> to confirm.
              </p>
              {duplicateProductionOverride ? (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-200">
                  Duplicate warning acknowledged. Clicking again will resend this content.
                </p>
              ) : null}
              <div className="mt-auto flex flex-col gap-3 pt-5 sm:flex-row sm:items-end">
                <Input
                  value={confirmSend}
                  onChange={(event) => setConfirmSend(event.target.value)}
                  placeholder="Type SEND"
                  className="bg-background shadow-sm sm:max-w-[11rem]"
                />
                <Button
                  className="shadow-sm"
                  variant="destructive"
                  onClick={() => handleSend("production")}
                  disabled={
                    isSendingTest ||
                    isSendingProduction ||
                    !productionReady ||
                    confirmSend.trim() !== "SEND" ||
                    (isSelectedCustomerMode && selectedCustomerList.length === 0)
                  }
                >
                  {isSendingProduction
                    ? isContactBookMode
                      ? "Sending production..."
                      : "Sending selected..."
                    : duplicateProductionOverride
                      ? "Send Anyway"
                      : isContactBookMode
                        ? "Send Production Contact Book"
                        : "Send Selected Customers"}
                </Button>
              </div>
            </section>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CampaignHistory({ campaigns }: { campaigns: any[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>useSend Campaign History</CardTitle>
        <CardDescription>
          Self-serve campaigns created from this page. Tested drafts can be reopened here and promoted to Production.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!campaigns.length ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No self-serve campaigns yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">When</th>
                  <th className="pb-2 pr-4 font-medium">Target</th>
                  <th className="pb-2 pr-4 font-medium">Name</th>
                  <th className="pb-2 pr-4 font-medium">Subject</th>
                  <th className="pb-2 pr-4 font-medium">Status</th>
                  <th className="pb-2 pr-4 font-medium">Anthem</th>
                  <th className="pb-2 pr-4 font-medium">useSend</th>
                  <th className="pb-2 font-medium">Operator</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => {
                  const canResume =
                    campaign.target === "test" &&
                    String(campaign.status ?? "").toLowerCase() === "sent" &&
                    Boolean(campaign.canAttemptResumeInAnthem);
                  const targetTone =
                    campaign.target === "production"
                      ? BADGE_TONES.red
                      : campaign.target === "custom"
                        ? BADGE_TONES.amber
                        : BADGE_TONES.slate;
                  const audienceLabel = campaign.audienceLabel || campaign.contactBookLabel;
                  return (
                    <tr key={String(campaign._id)} className="border-b align-top last:border-0">
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {formatDate(campaign.createdAt, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline" className={targetTone}>
                          {audienceLabel}
                        </Badge>
                        {campaign.target === "custom" && campaign.recipientCount ? (
                          <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                            {Number(campaign.recipientCount).toLocaleString()} recipients
                          </p>
                        ) : null}
                      </td>
                      <td className="max-w-[220px] break-words py-3 pr-4">{campaign.name}</td>
                      <td className="max-w-[300px] break-words py-3 pr-4">{campaign.subject}</td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline" className={campaignStatusTone(String(campaign.status ?? ""))}>
                          {formatStatusLabel(campaign.status)}
                        </Badge>
                        {campaign.error ? (
                          <p className="mt-1 max-w-[260px] break-words text-xs text-red-600 dark:text-red-400">
                            {String(campaign.error)}
                          </p>
                        ) : null}
                      </td>
                      <td className="py-3 pr-4 text-xs">
                        {canResume ? (
                          <Button asChild size="sm" variant="outline">
                            <Link to={`/marketing/emails/create?resume=${encodeURIComponent(String(campaign._id))}`}>
                              Resume / send Production
                            </Link>
                          </Button>
                        ) : campaign.target === "test" && String(campaign.status ?? "").toLowerCase() === "sent" ? (
                          <span className="text-muted-foreground">Re-paste HTML to send Production</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-3 pr-4 text-xs">
                        {campaign.useSendCampaignUrl ? (
                          <a
                            href={campaign.useSendCampaignUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline-offset-4 hover:underline"
                          >
                            Open campaign
                          </a>
                        ) : campaign.useSendCampaignId ? (
                          <span className="font-mono">{campaign.useSendCampaignId}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-3 text-xs text-muted-foreground">
                        {campaign.createdByName || campaign.createdByEmail || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SalesEmailsPage() {
  const { authUserId, userRole } = useAuthIdentity();
  const location = useLocation();
  const isCreateRoute = location.pathname.startsWith("/marketing/emails/create");
  const resumeCampaignId = React.useMemo(
    () => new URLSearchParams(location.search).get("resume"),
    [location.search],
  );
  const [search, setSearch] = React.useState("");
  const [isSyncingAudience, setIsSyncingAudience] = React.useState(false);
  const [syncSummary, setSyncSummary] = React.useState<null | {
    total: number;
    synced: number;
    skipped: number;
    failed: number;
    subscribed: number;
    unsubscribed: number;
    contactBookName: string | null;
  }>(null);
  const [isBackfillingOptIns, setIsBackfillingOptIns] = React.useState(false);
  const [backfillSummary, setBackfillSummary] = React.useState<null | {
    scanned: number;
    candidates: number;
    updated: number;
    remaining: number;
    skippedExplicitOptOut: number;
  }>(null);
  const [liveInfo, setLiveInfo] = React.useState<UseSendLiveInfo | null>(null);
  const [isLoadingLiveInfo, setIsLoadingLiveInfo] = React.useState(false);
  const [resumeCampaign, setResumeCampaign] = React.useState<any | null | undefined>(null);
  const syncUseSendMarketingContacts = useAction(
    (api.marketing as any).syncUseSendMarketingContacts,
  );
  const backfillCustomerMarketingOptIns = useMutation(
    (api.marketing as any).backfillCustomerMarketingOptIns,
  );
  const getUseSendSelfServeCampaignLiveInfo = useAction(
    (api.marketing as any).getUseSendSelfServeCampaignLiveInfo,
  );
  const getUseSendSelfServeCampaignForResume = useAction(
    (api.marketing as any).getUseSendSelfServeCampaignForResume,
  );
  const canSyncAudience = hasPermission(userRole, "admin:manage");

  const insights = useQuery(
    api.marketing.getEmailInsights,
    authUserId ? { authUserId, limit: 250 } : "skip",
  );
  const campaignConsole = useQuery(
    (api.marketing as any).getUseSendSelfServeCampaignConsole,
    authUserId ? { authUserId, limit: 50 } : "skip",
  );

  React.useEffect(() => {
    if (!authUserId || !resumeCampaignId) {
      setResumeCampaign(null);
      return;
    }

    let cancelled = false;
    setResumeCampaign(undefined);
    getUseSendSelfServeCampaignForResume({
      authUserId,
      campaignRecordId: resumeCampaignId,
    })
      .then((campaign: any | null) => {
        if (!cancelled) {
          setResumeCampaign(campaign);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setResumeCampaign(null);
          toast.error(error instanceof Error ? error.message : "Campaign resume failed");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authUserId, getUseSendSelfServeCampaignForResume, resumeCampaignId]);

  React.useEffect(() => {
    if (!authUserId || !campaignConsole?.canCreateCampaigns) return;

    let cancelled = false;
    setIsLoadingLiveInfo(true);
    getUseSendSelfServeCampaignLiveInfo({ authUserId })
      .then((result: UseSendLiveInfo) => {
        if (!cancelled) {
          setLiveInfo(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLiveInfo(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingLiveInfo(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authUserId, campaignConsole?.canCreateCampaigns, getUseSendSelfServeCampaignLiveInfo]);

  const topTypeCount = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of insights?.topEmailTypes ?? []) {
      counts.set(String(item.emailType), Number(item.count ?? 0));
    }
    return counts;
  }, [insights?.topEmailTypes]);

  const searchLower = search.trim().toLowerCase();
  const filteredLogs = React.useMemo(() => {
    const logs = insights?.recentLogs ?? [];
    if (!searchLower) return logs;
    return logs.filter((log: any) => {
      const fields = [
        String(log.to ?? ""),
        String(log.subject ?? ""),
        String(log.emailType ?? ""),
        String(log.status ?? ""),
      ];
      return fields.some((field) => field.toLowerCase().includes(searchLower));
    });
  }, [insights?.recentLogs, searchLower]);

  const handleAudienceSync = React.useCallback(async () => {
    if (!authUserId) {
      toast.error("Authentication required");
      return;
    }

    setIsSyncingAudience(true);
    try {
      const result = await syncUseSendMarketingContacts({
        authUserId,
        limit: 5000,
      });

      setSyncSummary({
        total: Number(result?.total ?? 0),
        synced: Number(result?.synced ?? 0),
        skipped: Number(result?.skipped ?? 0),
        failed: Number(result?.failed ?? 0),
        subscribed: Number(result?.subscribed ?? 0),
        unsubscribed: Number(result?.unsubscribed ?? 0),
        contactBookName:
          typeof result?.contactBookName === "string" ? result.contactBookName : null,
      });

      if (Number(result?.failed ?? 0) > 0) {
        const firstFailure = Array.isArray(result?.failures) ? result.failures[0] : null;
        toast.error(
          firstFailure?.error
            ? `Audience sync finished with failures: ${String(firstFailure.error)}`
            : "Audience sync finished with failures.",
        );
      } else {
        toast.success("useSend audience sync completed.");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Audience sync failed");
    } finally {
      setIsSyncingAudience(false);
    }
  }, [authUserId, syncUseSendMarketingContacts]);

  const handleBackfillOptIns = React.useCallback(async () => {
    if (!authUserId) {
      toast.error("Authentication required");
      return;
    }

    setIsBackfillingOptIns(true);
    try {
      const result = await backfillCustomerMarketingOptIns({
        authUserId,
        limit: 1000,
      });
      const summary = {
        scanned: Number(result?.scanned ?? 0),
        candidates: Number(result?.candidates ?? 0),
        updated: Number(result?.updated ?? 0),
        remaining: Number(result?.remaining ?? 0),
        skippedExplicitOptOut: Number(result?.skippedExplicitOptOut ?? 0),
      };
      setBackfillSummary(summary);
      toast.success(
        summary.remaining > 0
          ? `Restored ${summary.updated.toLocaleString()} opt-ins. Run again for ${summary.remaining.toLocaleString()} remaining.`
          : `Restored ${summary.updated.toLocaleString()} customer marketing opt-ins.`,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Marketing opt-in backfill failed");
    } finally {
      setIsBackfillingOptIns(false);
    }
  }, [authUserId, backfillCustomerMarketingOptIns]);

  if (isCreateRoute) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Sales • Emails</p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-balance">Create campaign</h1>
          </div>
          <Button asChild variant="outline">
            <Link to="/marketing/emails">Back to activity</Link>
          </Button>
        </div>

        <EmailsPageNav active="create" />
        <CampaignCreationGuide />

        {campaignConsole === undefined ? (
          <Card>
            <CardContent className="py-8 text-sm text-muted-foreground">
              Loading campaign tools...
            </CardContent>
          </Card>
        ) : campaignConsole?.canCreateCampaigns ? (
          <>
            {resumeCampaignId && resumeCampaign === undefined ? (
              <Card>
                <CardContent className="py-8 text-sm text-muted-foreground">
                  Loading saved campaign draft...
                </CardContent>
              </Card>
            ) : null}
            {resumeCampaignId && resumeCampaign === null ? (
              <Card className="border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/20">
                <CardHeader>
                  <CardTitle>Campaign draft unavailable</CardTitle>
                  <CardDescription>
                    This campaign could not be loaded in Anthem. Re-paste the approved HTML to continue.
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : null}
            {resumeCampaign?.resumeUnavailableReason ? (
              <Card className="border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/20">
                <CardHeader>
                  <CardTitle>Full HTML unavailable</CardTitle>
                  <CardDescription>{resumeCampaign.resumeUnavailableReason}</CardDescription>
                </CardHeader>
              </Card>
            ) : null}
            {resumeCampaignId && resumeCampaign === undefined ? null : (
              <CampaignComposer
                authUserId={authUserId}
                consoleData={campaignConsole}
                liveInfo={liveInfo}
                isLoadingLiveInfo={isLoadingLiveInfo}
                resumeCampaign={resumeCampaign ?? null}
              />
            )}
            <CampaignHistory campaigns={campaignConsole.campaigns ?? []} />
          </>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Campaign creation unavailable</CardTitle>
              <CardDescription>
                Your account cannot create self-serve campaigns from this console.
              </CardDescription>
            </CardHeader>
          </Card>
        )}
      </div>
    );
  }

  if (insights === undefined) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Sales • Emails</h1>
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, idx) => (
            <Card key={idx}>
              <CardContent className="space-y-3 p-6">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-7 w-24" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Recent Sends</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Array.from({ length: 6 }).map((_, idx) => (
              <Skeleton key={idx} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-balance">Sales • Emails</h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-pretty">
            Visibility into outbound email activity, template coverage, and marketing audience sync.
          </p>
        </div>
        {campaignConsole?.canCreateCampaigns ? (
          <Button asChild>
            <Link to="/marketing/emails/create">Create campaign</Link>
          </Button>
        ) : null}
      </div>

      <EmailsPageNav active="activity" />

      <section className="grid gap-4 md:grid-cols-6">
        <StatCard
          title="Total Logged"
          value={insights.stats.totalLogged}
          description="All email logs currently stored."
        />
        <StatCard
          title="Sent Today"
          value={insights.stats.sentToday}
          description="Successful sends since local midnight."
        />
        <StatCard
          title="Failed Today"
          value={insights.stats.failedToday}
          description="Send attempts that failed today."
        />
        <StatCard
          title="Whitelist Needed"
          value={insights.stats.whitelistOutreachNeeded ?? 0}
          description="Bounced recipients to contact personally."
        />
        <StatCard
          title="Sent (7 Days)"
          value={insights.stats.sentLast7Days}
          description="Successful sends across the last 7 days."
        />
        <StatCard
          title="Recipients Today"
          value={insights.stats.uniqueRecipientsToday}
          description="Unique recipient addresses reached today."
        />
      </section>

      {campaignConsole?.canCreateCampaigns ? (
        <>
          <Card>
            <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <CardTitle>Create campaigns</CardTitle>
                <CardDescription>Compose, test, and send in a focused workspace.</CardDescription>
              </div>
              <Button asChild>
                <Link to="/marketing/emails/create">Open creator</Link>
              </Button>
            </CardHeader>
          </Card>
          <CampaignHistory campaigns={campaignConsole.campaigns ?? []} />
        </>
      ) : null}

      {canSyncAudience ? (
        <Card>
          <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>useSend Audience Maintenance</CardTitle>
              <CardDescription>
                Admin-only repair actions for customer marketing eligibility and useSend audience sync.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleBackfillOptIns}
                disabled={isBackfillingOptIns || isSyncingAudience || !authUserId}
              >
                {isBackfillingOptIns ? "Restoring..." : "Restore Customer Opt-ins"}
              </Button>
              <Button
                onClick={handleAudienceSync}
                disabled={isSyncingAudience || isBackfillingOptIns || !authUserId}
              >
                {isSyncingAudience ? "Syncing..." : "Repair Audience Sync"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm text-muted-foreground">
              {backfillSummary ? (
                <p>
                  Restored {backfillSummary.updated.toLocaleString()} of{" "}
                  {backfillSummary.candidates.toLocaleString()} customer opt-ins across{" "}
                  {backfillSummary.scanned.toLocaleString()} profiles. Explicit unsubscribes
                  preserved: {backfillSummary.skippedExplicitOptOut.toLocaleString()}. Remaining:{" "}
                  {backfillSummary.remaining.toLocaleString()}.
                </p>
              ) : null}
              {syncSummary ? (
                <p>
                  {syncSummary.contactBookName
                    ? `Contact book: ${syncSummary.contactBookName}. `
                    : ""}
                  Processed {syncSummary.total} contacts. Synced {syncSummary.synced}, skipped{" "}
                  {syncSummary.skipped}, failed {syncSummary.failed}. Active subscribed contacts:{" "}
                  {syncSummary.subscribed}; inactive/unsubscribed contacts: {syncSummary.unsubscribed}.
                </p>
              ) : (
                <p>
                  Restore opt-ins if old customer records were imported as false, then repair audience sync if useSend contacts look stale.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            Recent Sends
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({searchLower
                ? `${filteredLogs.length} of ${insights.recentLogs.length}`
                : `${insights.recentLogs.length}`} {insights.recentLogs.length === 1 ? "email" : "emails"})
            </span>
          </CardTitle>
          <CardDescription>
            Most recent logged emails. Search by recipient, subject, type, or status.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search recipient, subject, status, type..."
            className="max-w-lg"
          />

          {!filteredLogs.length ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {insights.recentLogs.length
                ? "No emails match your search."
                : "No email logs yet. Logs will appear as emails are sent."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4 font-medium">When</th>
                    <th className="pb-2 pr-4 font-medium">To</th>
                    <th className="pb-2 pr-4 font-medium">Subject</th>
                    <th className="pb-2 pr-4 font-medium">Type</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 font-medium">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log: any) => (
                    <tr key={String(log._id)} className="border-b align-top last:border-0">
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {formatDate(log.createdAt, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="py-3 pr-4">{log.to}</td>
                      <td className="py-3 pr-4 max-w-[340px] break-words">{log.subject}</td>
                      <td className="py-3 pr-4">
                        <span className="font-mono text-xs">{log.emailType}</span>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline" className={statusTone(String(log.status ?? ""))}>
                          {formatStatusLabel(log.status)}
                        </Badge>
                      </td>
                      <td className="py-3 text-xs text-red-600 dark:text-red-400">
                        {log.error ? String(log.error) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Email Templates</CardTitle>
          <CardDescription>Known templates plus observed email types from logs.</CardDescription>
        </CardHeader>
        <CardContent>
          {!insights.templateCatalog.length ? (
            <p className="text-sm text-muted-foreground">No template metadata available.</p>
          ) : (
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {insights.templateCatalog.map((template: any) => (
                <div
                  key={template.emailType}
                  className="flex items-center justify-between rounded-md border px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{template.label}</p>
                    <p className="truncate text-xs text-muted-foreground">{template.emailType}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={BADGE_TONES.slate}>
                      {template.category}
                    </Badge>
                    <Badge variant="secondary">
                      {topTypeCount.get(String(template.emailType)) ?? 0}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
